import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { fileUrl } from "@/lib/api";
import { ChevronLeft, ChevronRight, X, Expand } from "lucide-react";

function Lightbox({ images, index, setIndex, onClose, alt }) {
  const n = images.length;
  const go = (d) => setIndex((index + d + n) % n);
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/90 grid place-items-center" data-testid="photo-lightbox"
      onClick={onClose}>
      <button data-testid="lightbox-close" onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"><X className="w-7 h-7" /></button>
      <img src={fileUrl(images[index])} alt={alt} className="max-h-[85vh] max-w-[92vw] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()} />
      {n > 1 && (
        <>
          <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-2.5"><ChevronLeft className="w-6 h-6" /></button>
          <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-2.5"><ChevronRight className="w-6 h-6" /></button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium">{index + 1} / {n}</div>
        </>
      )}
    </div>,
    document.body
  );
}

export function PhotoCarousel({ images = [], alt = "", heightClass = "h-40", enableLightbox = true, testid = "carousel" }) {
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const touchX = useRef(null);
  if (!images.length) return null;
  const n = images.length;
  const go = (d, e) => { e?.stopPropagation(); setIdx((i) => (i + d + n) % n); };

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div className={`relative ${heightClass} bg-secondary overflow-hidden group`} data-testid={testid}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <img src={fileUrl(images[idx])} alt={alt}
        className={`w-full h-full object-cover ${enableLightbox ? "cursor-zoom-in" : ""}`}
        onClick={() => enableLightbox && setOpen(true)} />

      {n > 1 && (
        <>
          <button data-testid={`${testid}-prev`} onClick={(e) => go(-1, e)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/45 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronLeft className="w-4 h-4" /></button>
          <button data-testid={`${testid}-next`} onClick={(e) => go(1, e)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/45 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronRight className="w-4 h-4" /></button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((u, i) => (
              <button key={`${i}_dot`} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-emerald-400" : "w-1.5 bg-white/70"}`} />
            ))}
          </div>
        </>
      )}

      <span className="absolute top-2 right-2 text-[10px] font-semibold bg-black/60 text-white px-2 py-0.5 rounded-full">{idx + 1} / {n}</span>
      {enableLightbox && (
        <button data-testid={`${testid}-expand`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="absolute top-2 left-2 bg-black/45 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Expand className="w-3.5 h-3.5" /></button>
      )}

      {open && <Lightbox images={images} index={idx} setIndex={setIdx} onClose={() => setOpen(false)} alt={alt} />}
    </div>
  );
}
